import React from 'react';
import {
    Table,
    TableHead,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    TableSortLabel,
    TextField,
    InputAdornment,
    Box,
    Button,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import type { User } from 'c-sdk';
import { Translate, useCommonTranslation } from 'c-translation';
import { TableRow } from 'c-components';
import {
    filterBuyerUsers,
    highlightParts,
    nextBuyerUsersSort,
    sortBuyerUsers,
} from './buyerUsersLib';

type BuyerUsersTableProps = {
    users: User[];
};

type OrderByField = 'id' | 'name' | 'email' | null;
type OrderDirection = 'asc' | 'desc';

const BuyerUsersTable: React.FC<BuyerUsersTableProps> = ({ users }) => {
    const t = useCommonTranslation();

    const [orderBy, setOrderBy] = React.useState<OrderByField>('id');
    const [order, setOrder] = React.useState<OrderDirection>('asc');
    const [searchVal, setSearchVal] = React.useState('');

    const handleSort = (field: OrderByField) => {
        if (!field) return;

        const next = nextBuyerUsersSort({ orderBy, order }, field);
        setOrderBy(next.orderBy);
        setOrder(next.order);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchVal('');
    };

    const searchInput = React.useMemo(
        () => (
            <TextField
                placeholder={t('Pagination.searchPlaceholder')}
                onChange={handleSearchChange}
                value={searchVal}
                size="small"
                sx={{ backgroundColor: 'background.paper' }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <Button
                                size="small"
                                aria-label={t('Common.clear') ?? 'Clear'}
                                variant="contained"
                                onClick={handleClearSearch}
                            >
                                <Typography variant="caption">
                                    <Translate path="Pagination.resetFilters" />
                                </Typography>
                            </Button>
                        </InputAdornment>
                    ),
                }}
            />
        ),
        [searchVal, t],
    );

    const filteredUsers = React.useMemo(
        () => filterBuyerUsers(users, searchVal),
        [users, searchVal],
    );

    const sortedUsers = React.useMemo(
        () => sortBuyerUsers(filteredUsers, orderBy, order),
        [filteredUsers, orderBy, order],
    );

    const highlightMatch = (text: string | null | undefined) => {
        const parts = highlightParts(text, searchVal);
        if (!parts) return text;

        return (
            <>
                {parts.map(({ part, match }, index) =>
                    match ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <strong key={index}>{part}</strong>
                    ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <span key={index}>{part}</span>
                    ),
                )}
            </>
        );
    };

    return (
        <>
            <Box mb={2}>{searchInput}</Box>

            <TableContainer component={Box}>
                <Table size="small">
                    <TableHead
                        sx={{
                            '& .MuiTableCell-head': {
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        <TableRow>
                            <TableCell sortDirection={orderBy === 'id' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'id'}
                                    direction={orderBy === 'id' ? order : 'asc'}
                                    onClick={() => handleSort('id')}
                                >
                                    {t('Modules.Auth.User.table.idCol')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'name' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    {t('Modules.Auth.User.table.nameCol')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'email' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'email'}
                                    direction={orderBy === 'email' ? order : 'asc'}
                                    onClick={() => handleSort('email')}
                                >
                                    {t('Modules.Auth.User.table.emailCol')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>{t('Modules.Auth.User.table.rolesCol')}</TableCell>
                            <TableCell>{t('Modules.Auth.User.table.agencyCol')}</TableCell>
                            <TableCell>{t('Modules.Auth.User.table.ownerCol')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody component={Paper}>
                        {sortedUsers.map(user => (
                            <TableRow key={user.id}>
                                <TableCell>{user.id}</TableCell>
                                <TableCell>{highlightMatch(user.name)}</TableCell>
                                <TableCell>{highlightMatch(user.email)}</TableCell>
                                <TableCell>
                                    {user.roles?.map(r => r.name).join(', ') ?? ''}
                                </TableCell>
                                <TableCell>
                                    {user.agencies?.map(a => a.name).join(', ') ?? ''}
                                </TableCell>
                                <TableCell>
                                    {user.owners?.map(o => o.name).join(', ') ?? ''}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default BuyerUsersTable;
